import { useParams } from 'react-router-dom';
import { MapPin, Globe, Mail, Calendar, Music, ListMusic, UserPlus, Clock, CheckCircle, XCircle, List, CalendarDays } from 'lucide-react';
import { useBandBySlug } from '../../hooks/useBands';
import { usePublicEvents } from '../../hooks/useEvents';
import { useBandSetlists } from '../../hooks/useSetlists';
import { useMyJoinRequestStatus, submitJoinRequest } from '../../hooks/useJoinRequests';
import { useAuth } from '../../hooks/useAuth';
import { formatDate, formatTime, getInitials } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import CalendarView from '../../components/ui/CalendarView';
import type { EventWithBand } from '../../hooks/useSearch';
import { useState, useEffect, useMemo } from 'react';

export default function BandPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const { band, loading } = useBandBySlug(slug);
  const { events } = usePublicEvents(band?.id);
  const { setlists } = useBandSetlists(band?.id, false);
  const { user } = useAuth();
  const { status: joinStatus, loading: joinStatusLoading } = useMyJoinRequestStatus(band?.id);
  const [activeTab, setActiveTab] = useState<'gigs' | 'setlists'>('gigs');
  const [gigView, setGigView] = useState<'list' | 'calendar'>('list');
  const [calendarMode, setCalendarMode] = useState<'month' | 'week' | 'day'>('month');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinSent, setJoinSent] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [isMember, setIsMember] = useState(false);

  // Check if user is already a member of this band
  useEffect(() => {
    if (!user || !band) return;
    async function checkMembership() {
      const { data } = await supabase
        .from('band_members')
        .select('id')
        .eq('band_id', band!.id)
        .eq('user_id', user!.id)
        .eq('is_accepted', true)
        .single();
      setIsMember(!!data);
    }
    checkMembership();
  }, [user, band]);

  // Convert events to EventWithBand format for calendar (must be before early returns - React hooks rules)
  const calendarEvents: EventWithBand[] = useMemo(() =>
    events.map((e) => ({
      ...e,
      band: band ? { id: band.id, name: band.name, slug: band.slug, logo_url: band.logo_url, location_name: band.location_name } : { id: '', name: '', slug: '', logo_url: null, location_name: null },
    })),
    [events, band]
  );

  const upcomingEvents = useMemo(() => events.filter((e) => new Date(e.start_time) >= new Date()), [events]);
  const pastEvents = useMemo(() => events.filter((e) => new Date(e.start_time) < new Date()), [events]);

  async function handleJoinRequest() {
    if (!user || !band) return;
    setJoinLoading(true);
    setJoinError('');
    const { error } = await submitJoinRequest(band.id, user.id, joinMessage);
    if (error) {
      setJoinError(error);
    } else {
      setJoinSent(true);
      setShowJoinModal(false);
      setJoinMessage('');
    }
    setJoinLoading(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Band Not Found</h1>
        <p className="text-gray-400">This band doesn't exist or isn't public.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Band header */}
      <div className="flex flex-col sm:flex-row items-start gap-6 mb-10">
        {band.logo_url ? (
          <img
            src={band.logo_url}
            alt={band.name}
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl object-cover"
          />
        ) : (
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl bg-gray-800 flex items-center justify-center">
            <span className="text-gray-400 font-bold text-3xl">{getInitials(band.name)}</span>
          </div>
        )}

        <div className="flex-1">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">{band.name}</h1>

          <div className="flex flex-wrap gap-4 text-gray-400 text-sm mb-3">
            {band.location_name && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {band.location_name}{band.location_country && `, ${band.location_country}`}
              </span>
            )}
            {band.website && (
              <a
                href={band.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300"
              >
                <Globe className="w-4 h-4" />
                Website
              </a>
            )}
            {band.contact_email && (
              <a
                href={`mailto:${band.contact_email}`}
                className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300"
              >
                <Mail className="w-4 h-4" />
                Contact
              </a>
            )}
          </div>

          {band.genres.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {band.genres.map((genre) => (
                <span key={genre} className="bg-indigo-900/40 text-indigo-300 text-xs px-3 py-1 rounded-full">
                  {genre}
                </span>
              ))}
            </div>
          )}

          {band.description && <p className="text-gray-300 leading-relaxed">{band.description}</p>}

          {/* Request to Join button */}
          {user && !joinStatusLoading && !isMember && (
            <div className="mt-4">
              {joinSent || joinStatus === 'pending' ? (
                <span className="inline-flex items-center gap-2 text-yellow-300 text-sm bg-yellow-900/30 px-4 py-2 rounded-lg">
                  <Clock className="w-4 h-4" />
                  Join request pending
                </span>
              ) : joinStatus === 'approved' ? (
                <span className="inline-flex items-center gap-2 text-green-300 text-sm bg-green-900/30 px-4 py-2 rounded-lg">
                  <CheckCircle className="w-4 h-4" />
                  You're a member of this band
                </span>
              ) : joinStatus === 'rejected' ? (
                <span className="inline-flex items-center gap-2 text-red-300 text-sm bg-red-900/30 px-4 py-2 rounded-lg">
                  <XCircle className="w-4 h-4" />
                  Join request was declined
                </span>
              ) : (
                <button
                  onClick={() => setShowJoinModal(true)}
                  className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                >
                  <UserPlus className="w-4 h-4" />
                  Request to Join
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Join Request Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Request to Join {band.name}</h2>
            <p className="text-gray-400 text-sm mb-4">
              The band owner will review your request. You can include an optional message.
            </p>

            {joinError && (
              <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
                {joinError}
              </div>
            )}

            <textarea
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value)}
              rows={3}
              placeholder="Tell the band why you'd like to join (optional)..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => { setShowJoinModal(false); setJoinError(''); }}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleJoinRequest}
                disabled={joinLoading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg text-sm font-medium transition"
              >
                {joinLoading ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('gigs')}
          className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition ${
            activeTab === 'gigs'
              ? 'border-indigo-400 text-indigo-400'
              : 'border-transparent text-gray-400 hover:text-gray-50'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Gigs ({upcomingEvents.length})
        </button>
        <button
          onClick={() => setActiveTab('setlists')}
          className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition ${
            activeTab === 'setlists'
              ? 'border-indigo-400 text-indigo-400'
              : 'border-transparent text-gray-400 hover:text-gray-50'
          }`}
        >
          <ListMusic className="w-4 h-4" />
          Setlists ({setlists.length})
        </button>
      </div>

      {/* Gigs Tab */}
      {activeTab === 'gigs' && (
        <div>
          {/* View toggle */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {gigView === 'list' ? 'Upcoming Gigs' : 'Gig Calendar'}
            </h2>
            <div className="flex bg-gray-800 rounded-lg p-0.5 text-sm">
              <button
                onClick={() => setGigView('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                  gigView === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setGigView('calendar')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                  gigView === 'calendar' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                Calendar
              </button>
            </div>
          </div>

          {/* Calendar view */}
          {gigView === 'calendar' && (
            <CalendarView
              events={calendarEvents}
              view={calendarMode}
              onViewChange={setCalendarMode}
            />
          )}

          {/* List view */}
          {gigView === 'list' && (
            <>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3 mb-10">
                  {upcomingEvents.map((event) => (
                    <div key={event.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        {/* Date badge */}
                        <div className="bg-indigo-600/15 border border-indigo-500/25 rounded-xl px-3 py-2 text-center flex-shrink-0 min-w-[65px]">
                          <div className="text-indigo-400 text-[10px] font-semibold uppercase">
                            {new Date(event.start_time).toLocaleDateString('en-GB', { month: 'short' })}
                          </div>
                          <div className="text-gray-50 text-xl font-bold leading-tight">
                            {new Date(event.start_time).getDate()}
                          </div>
                          <div className="text-indigo-400 text-[10px]">
                            {new Date(event.start_time).toLocaleDateString('en-GB', { weekday: 'short' })}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg">{event.title}</h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-400 text-sm mt-1">
                            <span>{formatTime(event.start_time)}</span>
                            {event.venue_name && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.venue_name}
                                {event.venue_address && ` - ${event.venue_address}`}
                              </span>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-gray-400 text-sm mt-2">{event.description}</p>
                          )}
                          {event.ticket_url && (
                            <a
                              href={event.ticket_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-2 text-indigo-400 hover:text-indigo-300 text-sm font-medium"
                            >
                              Get Tickets {event.ticket_price && `(${event.ticket_price})`}
                            </a>
                          )}
                          <span className={`inline-block mt-2 ml-2 text-xs px-2 py-0.5 rounded-full ${
                            event.event_type === 'gig'
                              ? 'bg-indigo-900/50 text-indigo-300'
                              : event.event_type === 'rehearsal'
                                ? 'bg-emerald-900/50 text-emerald-300'
                                : 'bg-gray-800 text-gray-400'
                          }`}>
                            {event.event_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No upcoming gigs scheduled.</p>
                </div>
              )}

              {pastEvents.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-gray-400">Past Gigs</h2>
                  <div className="space-y-3 opacity-60">
                    {pastEvents.slice(0, 5).map((event) => (
                      <div key={event.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-medium">{event.title}</h3>
                            {event.venue_name && (
                              <p className="text-gray-500 text-sm">{event.venue_name}</p>
                            )}
                          </div>
                          <div className="text-gray-500 text-sm">{formatDate(event.start_time)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Setlists Tab */}
      {activeTab === 'setlists' && (
        <div>
          {setlists.length === 0 ? (
            <div className="text-center py-12">
              <ListMusic className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No public setlists available.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {setlists.map((setlist) => (
                <div key={setlist.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h3 className="font-semibold text-lg mb-1">{setlist.title}</h3>
                  {setlist.description && (
                    <p className="text-gray-400 text-sm mb-3">{setlist.description}</p>
                  )}
                  {setlist.songs && setlist.songs.length > 0 && (
                    <div className="space-y-1">
                      {setlist.songs
                        .sort((a, b) => a.position - b.position)
                        .map((song, idx) => (
                          <div key={song.id} className="flex items-center gap-3 py-1.5 text-sm">
                            <span className="text-gray-500 w-6 text-right">{idx + 1}.</span>
                            <span className="text-gray-50">{song.title}</span>
                            {song.artist && <span className="text-gray-500">- {song.artist}</span>}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
